// src/logistique/vehicules/NouvelleMission.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Row, Col, Form, Button, Spinner, Modal, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn"><i className="ri-close-line fs-5"></i></button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   SEARCHABLE SELECT
   ============================================================ */
const SearchableSelect = ({
    options = [], value, onChange,
    placeholder = "Sélectionner...", searchPlaceholder = "Rechercher...",
    labelKey = "label", valueKey = "value",
    emptyMessage = "Aucun résultat", icon = "ri-search-line",
    required = false, disabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false); setSearch('');
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        const s = search.toLowerCase();
        return options.filter(o => String(o[labelKey] || '').toLowerCase().includes(s));
    }, [options, search, labelKey]);

    const selected = options.find(o => String(o[valueKey]) === String(value));

    return (
        <div className="searchable-select" ref={wrapperRef}>
            <div
                className={`searchable-select-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setOpen(o => !o)}
                tabIndex={0}
            >
                <i className={`${icon} me-2`}></i>
                <span className={selected ? '' : 'text-muted'}>
                    {selected ? selected[labelKey] : placeholder}
                </span>
                <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line ms-auto`}></i>
            </div>

            {open && (
                <div className="searchable-select-menu">
                    <div className="searchable-select-search">
                        <i className="ri-search-line"></i>
                        <input
                            type="text" autoFocus
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="searchable-select-options">
                        {filtered.length === 0 ? (
                            <div className="searchable-select-empty">{emptyMessage}</div>
                        ) : (
                            filtered.map((opt, idx) => (
                                <div
                                    key={`${opt[valueKey]}-${idx}`}
                                    className={`searchable-select-option ${String(opt[valueKey]) === String(value) ? 'selected' : ''}`}
                                    onClick={() => { onChange(opt[valueKey]); setOpen(false); setSearch(''); }}
                                >
                                    {opt[labelKey]}
                                    {String(opt[valueKey]) === String(value) && (
                                        <i className="ri-check-line ms-auto" style={{ color: COLORS.primary }}></i>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            {required && (
                <input type="text" value={value || ''} required onChange={() => {}} tabIndex={-1}
                    style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
            )}
        </div>
    );
};

/* ============================================================
   MODAL : CRÉATION RAPIDE D'UTILISATEUR (inline)
   ============================================================ */
const ModalNouvelUtilisateur = ({ show, onClose, onCreated }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        nom_utilisateur: '',
        prenom_utilisateur: '',
        email: '',
        mot_de_passe: '',
        role: 'commercial'
    });

    const reset = () => {
        setForm({ nom_utilisateur: '', prenom_utilisateur: '', email: '', mot_de_passe: '', role: 'commercial' });
        setError('');
    };

    const handleClose = () => { if (!saving) { reset(); onClose(); } };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.nom_utilisateur.trim() || !form.prenom_utilisateur.trim()) {
            setError("Nom et prénom sont obligatoires");
            return;
        }
        if (!form.email.trim()) {
            setError("L'email est obligatoire");
            return;
        }
        if (!form.mot_de_passe || form.mot_de_passe.length < 6) {
            setError("Le mot de passe doit contenir au moins 6 caractères");
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/utilisateurs.php?action=create`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const result = await res.json();
            if (result.success) {
                onCreated({
                    id_utilisateur: result.id,
                    nom_utilisateur: form.nom_utilisateur,
                    prenom_utilisateur: form.prenom_utilisateur,
                    email: form.email
                });
                reset();
                onClose();
            } else {
                setError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop={saving ? 'static' : true}>
            <Modal.Header closeButton={!saving} className="card-header-gradient">
                <Modal.Title className="text-white">
                    <i className="ri-user-add-line me-2"></i>
                    Créer un utilisateur
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <div className="alert alert-danger py-2 px-3 small mb-3">{error}</div>}
                    <p className="text-muted small mb-3">
                        L'utilisateur sera créé et sélectionné automatiquement comme conducteur.
                    </p>
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="form-label-modern">Nom <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    value={form.nom_utilisateur}
                                    onChange={(e) => setForm({ ...form, nom_utilisateur: e.target.value })}
                                    disabled={saving}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="form-label-modern">Prénom <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    value={form.prenom_utilisateur}
                                    onChange={(e) => setForm({ ...form, prenom_utilisateur: e.target.value })}
                                    disabled={saving}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="form-label-modern">Email <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    disabled={saving}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="form-label-modern">Mot de passe <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="password"
                                    value={form.mot_de_passe}
                                    onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })}
                                    placeholder="Min. 6 caractères"
                                    disabled={saving}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="form-label-modern">Rôle</Form.Label>
                                <Form.Select
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    disabled={saving}
                                >
                                    <option value="admin">Administrateur</option>
                                    <option value="gestionnaire">Gestionnaire</option>
                                    <option value="commercial">Commercial</option>
                                    <option value="magasinier">Magasinier</option>
                                    <option value="autres">Autres</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={handleClose} disabled={saving}>Annuler</Button>
                    <Button type="submit" className="btn-modern-primary" disabled={saving} style={{ minWidth: '160px' }}>
                        {saving ? <><Spinner size="sm" className="me-2" />Création...</> : <><i className="ri-save-line me-2"></i>Créer et sélectionner</>}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

/* ============================================================
   PAGE : NOUVELLE MISSION
   ============================================================ */
export default function NouvelleMission() {
    const navigate = useNavigate();
    const [vehicules, setVehicules] = useState([]);
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showUserModal, setShowUserModal] = useState(false);

    // Toast
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    // ✅ Rôle de l'utilisateur connecté
    const currentUser = useMemo(() => {
        try {
            const raw = localStorage.getItem("user");
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    }, []);
    const currentRole = currentUser?.role || null;
    const peutCreerUtilisateur = currentRole && currentRole !== 'autres';

    const [form, setForm] = useState({
        id_vehicule: '',
        id_utilisateur_conducteur: '',
        date_debut: '',
        date_fin_prevue: '',
        destination: '',
        kilometrage_debut: 0,
        statut: 'planifiee',
        commentaire: ''
    });

    useEffect(() => {
        fetchVehicules();
        fetchUtilisateurs();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchVehicules = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/vehicules.php?statut=disponible`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            setVehicules(result.data || []);
        } catch (err) { console.error(err); }
    };

    const fetchUtilisateurs = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/utilisateurs.php?action=list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            setUtilisateurs(result.data || []);
        } catch (err) { console.error(err); }
    };

    /* ============================================================
       OPTIONS
       ============================================================ */
    const vehiculeOptions = useMemo(() =>
        vehicules.map(v => ({
            value: v.id_vehicule,
            label: `${v.immatriculation} — ${v.marque || ''} ${v.modele || ''}`.trim()
        })), [vehicules]
    );

    const utilisateurOptions = useMemo(() =>
        utilisateurs.map(u => ({
            value: u.id_utilisateur,
            label: `${u.prenom_utilisateur || ''} ${u.nom_utilisateur || ''}`.trim() + (u.email ? ` (${u.email})` : '')
        })), [utilisateurs]
    );

    /* ============================================================
       CRÉATION UTILISATEUR → callback
       ============================================================ */
    const handleUtilisateurCree = async (newUser) => {
        await fetchUtilisateurs();
        setForm(prev => ({ ...prev, id_utilisateur_conducteur: newUser.id_utilisateur }));
        showToast('success', 'Utilisateur créé',
            `${newUser.prenom_utilisateur} ${newUser.nom_utilisateur} a été créé et sélectionné.`);
    };

    /* ============================================================
       SOUMISSION
       ============================================================ */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.id_vehicule || !form.id_utilisateur_conducteur || !form.date_debut || !form.date_fin_prevue) {
            setError('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/vehicules.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'create_mission', ...form })
            });
            const result = await res.json();
            if (result.success) {
                showToast('success', 'Mission créée', 'La mission a été enregistrée avec succès.');
                setTimeout(() => navigate('/logistique/missions'), 1500);
            } else {
                setError(result.message || 'Impossible de créer la mission');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                <div className="container-fluid">
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper"><i className="ri-route-line"></i></div>
                            <div>
                                <h5 className="header-title">Nouvelle mission</h5>
                                <p className="text-muted small mb-0">Planifier une mission pour un véhicule</p>
                            </div>
                        </div>
                        <Button className="btn-modern-outline" onClick={() => navigate('/logistique/missions')}>
                            <i className="ri-arrow-left-line"></i> Retour
                        </Button>
                    </div>

                    {error && <div className="alert alert-danger mb-4">{error}</div>}

                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-route-line me-2"></i> Informations de la mission
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">

                                            {/* ===== VÉHICULE ===== */}
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Véhicule <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <SearchableSelect
                                                        options={vehiculeOptions}
                                                        value={form.id_vehicule}
                                                        onChange={(v) => setForm({ ...form, id_vehicule: v })}
                                                        placeholder="Sélectionner un véhicule"
                                                        searchPlaceholder="Immatriculation, marque..."
                                                        emptyMessage="Aucun véhicule disponible"
                                                        icon="ri-roadster-line"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>

                                            {/* ===== CONDUCTEUR ===== */}
                                            <Col md={6}>
                                                <Form.Group>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <Form.Label className="form-label-modern mb-0">
                                                            Conducteur <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        {/* ✅ Bouton masqué pour le rôle "autres" */}
                                                        {peutCreerUtilisateur && (
                                                            <button
                                                                type="button"
                                                                className="btn-link-create"
                                                                onClick={() => setShowUserModal(true)}
                                                            >
                                                                <i className="ri-add-circle-line me-1"></i>
                                                                Créer un utilisateur
                                                            </button>
                                                        )}
                                                    </div>
                                                    <SearchableSelect
                                                        options={utilisateurOptions}
                                                        value={form.id_utilisateur_conducteur}
                                                        onChange={(v) => setForm({ ...form, id_utilisateur_conducteur: v })}
                                                        placeholder="Sélectionner un conducteur"
                                                        searchPlaceholder="Nom, email..."
                                                        emptyMessage="Aucun utilisateur trouvé"
                                                        icon="ri-user-line"
                                                        required
                                                    />
                                                    {utilisateurs.length === 0 && (
                                                        <Form.Text className="text-muted small">
                                                            {peutCreerUtilisateur
                                                                ? "Aucun utilisateur. Cliquez sur « Créer un utilisateur »."
                                                                : "Aucun utilisateur disponible. Contactez un administrateur."}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>

                                            {/* ===== DATES ===== */}
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Date début <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="datetime-local"
                                                        value={form.date_debut}
                                                        onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Date fin prévue <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="datetime-local"
                                                        value={form.date_fin_prevue}
                                                        onChange={(e) => setForm({ ...form, date_fin_prevue: e.target.value })}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>

                                            {/* ===== DESTINATION + KM ===== */}
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Destination</Form.Label>
                                                    <Form.Control
                                                        value={form.destination}
                                                        onChange={(e) => setForm({ ...form, destination: e.target.value })}
                                                        placeholder="Ex: Port-Gentil, Libreville..."
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Km départ</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            value={form.kilometrage_debut}
                                                            onChange={(e) => setForm({ ...form, kilometrage_debut: parseInt(e.target.value) || 0 })}
                                                        />
                                                        <InputGroup.Text className="bg-light">km</InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* ===== COMMENTAIRE ===== */}
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Commentaire</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={3}
                                                        value={form.commentaire}
                                                        onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
                                                        placeholder="Notes internes..."
                                                    />
                                                </Form.Group>
                                            </Col>

                                            {/* ===== ACTIONS ===== */}
                                            <Col md={12}>
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button
                                                        type="button"
                                                        className="btn-modern-outline"
                                                        onClick={() => navigate('/logistique/missions')}
                                                        disabled={saving}
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        className="btn-modern-primary"
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <><Spinner size="sm" className="me-2" />Création...</>
                                                        ) : (
                                                            <><i className="ri-save-line me-2"></i>Créer la mission</>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL CRÉATION UTILISATEUR (uniquement si autorisé) ===== */}
            {peutCreerUtilisateur && (
                <ModalNouvelUtilisateur
                    show={showUserModal}
                    onClose={() => setShowUserModal(false)}
                    onCreated={handleUtilisateurCree}
                />
            )}

            {/* ===== STYLES ===== */}
            <style>{`
                .header-modern { background: white; padding: 1.2rem 1.8rem; border-left: 4px solid ${COLORS.secondary}; border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary}); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .card-header-gradient { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; padding: 1rem 1.5rem; }
                .form-label-modern { font-weight: 600; color: #334155; font-size: 0.85rem; }
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; color: #4b5563; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary:disabled { opacity: 0.7; cursor: not-allowed; }
                .btn-link-create { background: transparent; border: none; color: ${COLORS.secondary}; font-size: 0.8rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; padding: 0; }
                .btn-link-create:hover { color: ${COLORS.primary}; text-decoration: underline; }

                /* ===== SEARCHABLE SELECT ===== */
                .searchable-select { position: relative; }
                .searchable-select-trigger { display: flex; align-items: center; padding: 0.6rem 0.9rem; border: 2px solid #e2e8f0; border-radius: 10px; background: white; cursor: pointer; font-size: 0.9rem; }
                .searchable-select-trigger.open { border-color: ${COLORS.secondary}; box-shadow: 0 0 0 3px rgba(33,172,205,0.12); }
                .searchable-select-trigger i:first-child { color: ${COLORS.primary}; }
                .searchable-select-menu { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: white; border-radius: 12px; box-shadow: 0 12px 32px rgba(0,0,0,0.15); z-index: 1000; overflow: hidden; }
                .searchable-select-search { display: flex; align-items: center; gap: 8px; padding: 0.7rem 1rem; border-bottom: 1px solid #f1f5f9; }
                .searchable-select-search i { color: #94a3b8; }
                .searchable-select-search input { border: none; outline: none; flex: 1; font-size: 0.9rem; }
                .searchable-select-options { max-height: 260px; overflow-y: auto; padding: 0.4rem; }
                .searchable-select-option { padding: 0.55rem 0.8rem; border-radius: 8px; cursor: pointer; font-size: 0.88rem; display: flex; align-items: center; }
                .searchable-select-option:hover { background: #f1f5f9; }
                .searchable-select-option.selected { background: ${COLORS.primary}10; color: ${COLORS.primary}; font-weight: 600; }
                .searchable-select-empty { padding: 1rem; text-align: center; color: #94a3b8; font-size: 0.85rem; }

                /* ===== TOAST ===== */
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-progress { height: 3px; animation: shrink linear forwards; }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
            `}</style>
        </>
    );
}